import {
  EMPLOYER_CHANGE_RESTRICTION_PERIOD_LABEL,
  EXAM_GRADE_1YEAR_LABEL,
  EXAM_GRADE_FINAL_LABEL,
  TMasterCompanyMainSkill,
} from "@/api/entities/master-company-main-skill.entity";
import { IconPlusCircle } from "@/assets/icons";
import { Button } from "@/components/elements/Button";
import { EMPTY_STR } from "@/constants/string";
import { CompanyMainSkillFormModal } from "@/features/master-data/components/CompanySpecificIndustry/CompanyMainSkillFormModal";
import { HiddenCompanyMainSkillModal } from "@/features/master-data/components/CompanySpecificIndustry/HiddenCompanyMainSkillModal";
import { useVisible } from "@/hooks/useVisible";
import { background, divider, text } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { Box, Stack, styled, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { FC, useState } from "react";

type TCompanyMainSkillFormTableProps = {
  companyMainSkillRows: TMasterCompanyMainSkill[];
  setCompanyMainSkillRows: React.Dispatch<React.SetStateAction<TMasterCompanyMainSkill[]>>;
  existingCompanySpecificIndustryId: number;
  existingCompanySpecificBusinessClassificationId: number;
  existingCompanySpecificBusinessClassificationName: string;
};

export const CompanyMainSkillFormTable: FC<TCompanyMainSkillFormTableProps> = ({
  companyMainSkillRows,
  setCompanyMainSkillRows,
  existingCompanySpecificIndustryId,
  existingCompanySpecificBusinessClassificationId,
  existingCompanySpecificBusinessClassificationName,
}) => {
  const companyMainSkillFormModal = useVisible();
  const [selectedCompanyMainSkill, setSelectedCompanyMainSkill] = useState<TMasterCompanyMainSkill>();
  const [hiddenCompanyMainSkill, setHiddenCompanyMainSkill] = useState<TMasterCompanyMainSkill>();

  return (
    <>
      <Stack gap={1} flexDirection="column">
        <StyledTable>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>主たる技能</TableCell>
                <TableCell>試験名（1年経過時まで）</TableCell>
                <TableCell>級（1年経過時まで）</TableCell>
                <TableCell>試験名（育成就労終了まで）</TableCell>
                <TableCell>級（育成就労終了まで）</TableCell>
                <TableCell>育成就労実施者の変更を制限する期間(分野単位)</TableCell>
                <TableCell>有効／無効</TableCell>
                <TableCell>編集</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {companyMainSkillRows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell sx={{ minWidth: "160px" }}>
                    <Typography color={row.isHidden ? text.tertiary : text.primary} variant="body14">
                      {row.mainSkillName || EMPTY_STR.TEXT}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: "200px" }}>
                    <Typography color={row.isHidden ? text.tertiary : text.primary} variant="body14">
                      {row.examName1Year || EMPTY_STR.TEXT}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: "100px" }}>
                    <Typography color={row.isHidden ? text.tertiary : text.primary} variant="body14">
                      {(row.examGrade1Year && EXAM_GRADE_1YEAR_LABEL[row.examGrade1Year]) || EMPTY_STR.TEXT}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: "200px" }}>
                    <Typography color={row.isHidden ? text.tertiary : text.primary} variant="body14">
                      {row.examNameFinal || EMPTY_STR.TEXT}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: "100px" }}>
                    <Typography color={row.isHidden ? text.tertiary : text.primary} variant="body14">
                      {(row.examGradeFinal && EXAM_GRADE_FINAL_LABEL[row.examGradeFinal]) || EMPTY_STR.TEXT}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: "100px" }}>
                    <Typography color={row.isHidden ? text.tertiary : text.primary} variant="body14">
                      {(row.employerChangeRestrictionPeriod && EMPLOYER_CHANGE_RESTRICTION_PERIOD_LABEL[row.employerChangeRestrictionPeriod]) ||
                        EMPTY_STR.TEXT}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: "100px" }}>
                    <Button variant="outline" size="sm" onClick={() => setHiddenCompanyMainSkill(row)}>
                      {row.isHidden ? "有効化" : "無効化"}
                    </Button>
                  </TableCell>
                  <TableCell sx={{ minWidth: "80px" }}>
                    <Button
                      size="sm"
                      onClick={() => {
                        setSelectedCompanyMainSkill(row);
                        companyMainSkillFormModal.open();
                      }}
                    >
                      編集
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </StyledTable>
        <AddRowButton
          onClick={() => {
            setSelectedCompanyMainSkill(undefined);
            companyMainSkillFormModal.open();
          }}
        >
          <IconPlusCircle />
          <Typography variant="cap12">{"行を追加する"}</Typography>
        </AddRowButton>
      </Stack>

      {companyMainSkillFormModal.visible && (
        <CompanyMainSkillFormModal
          onClose={() => {
            companyMainSkillFormModal.close();
            setSelectedCompanyMainSkill(undefined);
          }}
          isOpen={companyMainSkillFormModal.visible}
          selectedCompanyMainSkill={selectedCompanyMainSkill}
          existingCompanySpecificIndustryId={existingCompanySpecificIndustryId}
          existingCompanySpecificBusinessClassificationId={existingCompanySpecificBusinessClassificationId}
          existingCompanySpecificBusinessClassificationName={existingCompanySpecificBusinessClassificationName}
          setCompanyMainSkillRows={setCompanyMainSkillRows}
        />
      )}

      <HiddenCompanyMainSkillModal
        hiddenCompanyMainSkill={hiddenCompanyMainSkill}
        setHiddenCompanyMainSkill={setHiddenCompanyMainSkill}
        setCompanyMainSkillRows={setCompanyMainSkillRows}
        existingCompanySpecificIndustryId={existingCompanySpecificIndustryId}
      />
    </>
  );
};

const AddRowButton = styled(Box)`
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
`;

const StyledTable = styled(Box)`
  border-radius: ${rounded.sm};
  overflow: auto;
  max-height: 35vh;
  height: fit-content !important;
  border: 1px solid ${divider.middle};
  background-color: ${background.white};
  table {
    border-collapse: separate;
  }

  * {
    box-shadow: none;
  }
  .MuiTableCell-head {
    padding: 8px !important;
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
    text-align: left;
    position: sticky;
    background-color: white;
    z-index: 500;
    top: 0;
  }
  .MuiTableCell-body {
    font-size: 14px;
    font-weight: 400;
    line-height: 28px;
  }
  .MuiTableRow-root {
    background-color: ${background.white};
    box-shadow: none;
    cursor: initial;
    .MuiTableCell-root {
      padding: 4px 8px;
      border-color: ${divider.middle};
      opacity: 1;
    }
    &:not(.MuiTableRow-head):last-child {
      .MuiTableCell-root {
        border-bottom: 0;
      }
    }
  }
  .MuiTableRow-root:not(.MuiTableRow-head):hover {
    &,
    td {
      background-color: ${background.white};
    }
  }
`;
